import Layout from 'components/layout';

import React, { type FC, useEffect, type PropsWithChildren } from 'react';

import { css } from '@emotion/react';
import { isDefined } from '@lucid/utils';
import { withImage, useSmartlingStrings } from '@lucid/hooks';
import { reactHTMLParser, Heading, Box, Text, Link, Container, Image, LucidHead } from '@lucid/components';
import { graphql, type HeadFC } from 'gatsby';

import type { Lucid } from 'types/types';
import JumplinkTracker from 'components/jumplink-tracker/jumplink-tracker';
import SignUpCta from 'components/sign-up-cta/sign-up-cta';
import { useDiscoveryPagesByType } from 'hooks/hooks';

const dividerCss = (theme: Lucid.Theme.Brand) =>
  css({
    flex: '0 1 50%',
    margin: `${theme.space.s48}px ${theme.space.s0}`,
    padding: theme.space.s0,
    marginRight: '30%',
    border: 0,
    borderTop: `2px solid ${theme.colors.primary}`,
    [theme.breakpoints.bp900.min]: {
      flex: '0 1 100%',
      margin: `${theme.space.s80}px ${theme.space.s0}`,
    },
  });

const leftBlockCss = (theme: Lucid.Theme.Brand) =>
  css({
    flex: '0 1 100%',
    maxWidth: '100%',
    textAlign: 'left',
    marginBottom: theme.space.s32,
    [theme.breakpoints.bp900.min]: {
      flex: '0 1 29%',
      maxWidth: '29%',
      textAlign: 'right',
      position: 'relative',
    },
  });

const rightBlockCss = (theme: Lucid.Theme.Brand) =>
  css({
    flex: '0 1 100%',
    maxWidth: '100%',
    marginBottom: theme.space.s32,
    [theme.breakpoints.bp900.min]: {
      flex: '0 1 59%',
      maxWidth: '59%',
    },
  });

const readTimeCss = (theme: Lucid.Theme.Brand) =>
  css({
    lineHeight: theme.lineHeights.lh1,
    fontSize: '0.875rem',
    color: theme.colors.coolGrey[70],
    fontWeight: 400,
    marginTop: theme.space.s0,
    marginBottom: theme.space.s32,
    '.discoveryHeader + &': {
      marginTop: -theme.space.s48!,
      marginBottom: theme.space.s64,
    },
  });

const userContentCss = (theme: Lucid.Theme.Brand) =>
  css([
    readTimeCss(theme),
    {
      'p, li': {
        margin: `${theme.space.s0} ${theme.space.s0} ${theme.space.s16}px`,
        fontSize: theme.fontSizes.fs20,
        color: theme.colors.coolGrey[100],
        lineHeight: theme.lineHeights.lh1_35,
      },
      'a:not(.img-link)': {
        fontWeight: theme.fontWeights.fw500,
      },
      '.gatsby-image-wrapper': {
        margin: `${theme.space.s40}px ${theme.space.s0}`,
      },
      '.lazyload-wrapper': {
        width: '100%',
      },
      'img:not[.overwrite-css]': {
        height: 'auto',
        width: '100%',
        margin: `${theme.space.s24}px ${theme.space.s0}`,
      },
      'picture > img': {
        margin: theme.space.s0,
      },
      blockquote: {
        borderLeft: `2px solid ${theme.colors.orange[45]}`,
        paddingLeft: theme.space.s32,
        margin: `${theme.space.s16}px ${theme.space.s0}`,
      },
    },
  ]);

const Discovery: FC<PropsWithChildren<Lucid.Context.PageProps>> = ({ data, pageContext }) => {
  const { directus } = data;
  const node = directus.node;
  const { nodeTranslations, featured_image } = node;
  const translatedData = node.translations?.[0] as Lucid.Directus.Generated.Discovery_Translations;

  const discoveryType = node.discovery_type as Lucid.Directus.Generated.Discovery_Type;
  const dtTranslations = discoveryType?.translations?.[0];

  const { heading, intro, html_sections, pre_footer_heading, pre_footer_html } = translatedData;

  const { shortCode, urlLangPrefix, localeId, pageType, nid, shouldHideSsr, metatags, product } = pageContext;
  const withSmartling = useSmartlingStrings({
    keys: [
      'mktg_discovery_min_read',
      'mktg_ct_disc_bottom_cta_preblurb_default',
      'mktg_generics_sign_up_free',
      'mktg_ct_disc_mid_page_cta_preblurb-scale',
      'mktg_ct_disc_bottom_cta_preblurb_default-scale',
    ],
    shortCode,
  });

  const sameTypeDiscoveryPages = useDiscoveryPagesByType({
    discoveryTypeFilter: discoveryType?.id,
    excludeId: nid,
    languageKey: shortCode,
  });
  const sameTypeDiscoveryPageLinks = (
    sameTypeDiscoveryPages.map(page => {
      const resource = page.translations?.[0] as Lucid.Directus.Generated.Discovery_Translations;
      if (!resource || !resource?.heading || !resource?.path?.url) return null;

      return {
        default_link_text: resource?.heading,
        final_url: resource?.path?.url,
      };
    }) || []
  ).filter(isDefined);

  const discoveryLinks = (dtTranslations?.discovery_links?.map(dLink => dLink.links_id) || []).filter(isDefined);

  const additionalResources = [...sameTypeDiscoveryPageLinks, ...discoveryLinks];

  const discoverySections = html_sections?.map(
    section => section.discovery_section_id,
  ) as Lucid.Directus.Generated.Discovery_Section[];

  const charCount = discoverySections
    ?.map(({ html }) => html)
    .join('')
    .replace(/(<(?:.|\n)*?>|\s)/gm, '').length; // Remove all HTML tags + whitespace

  useEffect(() => {
    // Shift minutes read before YouTube videos if one exists
    const iframe = document.querySelector('.discoveryHeader iframe');

    if (iframe && iframe.parentElement) {
      const readElement = document.querySelector('.discoveryReadTime');

      if (readElement) {
        iframe.parentElement.insertBefore(readElement, iframe);
      }
    }
  }, []);

  const block = 'discovery';

  return (
    <Layout
      localeId={localeId}
      metatags={metatags}
      nid={nid}
      nodeTranslations={nodeTranslations}
      pageType={pageType}
      product={product}
      shortCode={shortCode}
      shouldHideSsr={shouldHideSsr}
      urlLangPrefix={urlLangPrefix}
    >
      <Container
        css={theme => ({
          display: 'flex',
          paddingTop: `${theme.space.s80}px !important`,
          flexWrap: 'wrap',
          zIndex: 'auto',
          '*': {
            WebkitFontSmoothing: 'initial',
          },
          '&::before': {
            position: 'absolute',
            left: '-100%',
            top: -theme.space.s64!,
            bottom: 0,
            right: '65%',
            backgroundColor: theme.colors.coolGrey[0],
            zIndex: theme.zIndices.zNegative1,
            [theme.breakpoints.bp900.min]: {
              content: '""',
            },
          },
          strong: {
            fontWeight: theme.fontWeights.fw800,
          },
        })}
        extraPadding={3}
        id="top"
      >
        <Box
          css={theme => [
            {
              order: -1,
            },
            leftBlockCss(theme),
          ]}
        >
          <Image
            css={theme => ({
              padding: theme.space.s0,
              marginBottom: theme.space.s64,
              width: '100%',
              maxWidth: '500px',
              [theme.breakpoints.bp900.min]: {
                paddingLeft: theme.space.s64,
              },
            })}
            dataElementPageLocation="hero"
            imageAlt={featured_image?.translations?.[0]?.alt || ''}
            imageSrc={withImage(featured_image)}
            loading="lazy"
          />
        </Box>
        <Box
          css={theme => [
            {
              alignSelf: 'flex-end',
            },
            rightBlockCss(theme),
            theme.shared.htmlParserStyles(theme),
          ]}
        >
          <Heading
            as="h1"
            css={theme => [
              shortCode === 'ja' && {
                [theme.breakpoints.bp500.min]: {
                  fontSize: theme.fontSizes.fs40,
                },
                [theme.breakpoints.bp768.min]: {
                  fontSize: theme.fontSizes.fs48,
                },
                [theme.breakpoints.bp1220.min]: {
                  fontSize: theme.fontSizes.fs64,
                },
              },
            ]}
            variant="hero3XL700"
          >
            {heading}
          </Heading>
        </Box>
        <hr
          css={theme => [
            dividerCss(theme),
            {
              display: 'none',
              [theme.breakpoints.bp900.min]: {
                display: 'block',
              },
            },
          ]}
          id="discoveryTop"
        />
        <Box css={leftBlockCss}>
          <JumplinkTracker discoverySections={discoverySections} sectionSelector={`.${block}Section`} />
        </Box>
        <Box as="article" css={rightBlockCss}>
          {intro && (
            <Box
              key={`${intro}Key-${shortCode}`}
              css={theme => [
                userContentCss(theme),
                {
                  margin: theme.space.s0,
                  p: {
                    fontSize: theme.fontSizes.fs20,
                    fontWeight: theme.fontWeights.fw300,
                    color: theme.colors.coolGrey[100],
                    [theme.breakpoints.bp900.min]: {
                      fontSize: theme.fontSizes.fs24,
                    },
                  },
                },
                theme.shared.htmlParserStyles(theme),
              ]}
            >
              {reactHTMLParser(intro)}
            </Box>
          )}
          <Text css={readTimeCss}>
            {withSmartling('mktg_discovery_min_read', {
              time: String(Math.round(charCount / 1000)),
            })}
          </Text>
          <Box
            css={theme => ({
              borderTop: `2px solid ${theme.colors.coolGrey[60]}`,
              borderBottom: `2px solid ${theme.colors.coolGrey[60]}`,
              paddingTop: theme.space.s64,
              paddingBottom: theme.space.s16,
              marginTop: theme.space.s16,
              marginBottom: theme.space.s48,
              [theme.breakpoints.bp900.min]: {
                marginBottom: theme.space.s120,
              },
            })}
          >
            <Text
              css={theme => ({
                marginBottom: theme.space.s40,
              })}
              variant="paragraphM400"
            >
              {dtTranslations?.want_text ?? withSmartling('mktg_ct_disc_bottom_cta_preblurb_default-scale')}
            </Text>
            <Box>
              <SignUpCta isDiscovery beaconElementDetail="register-top" beaconElementLocation="body">
                {dtTranslations?.make_text ?? withSmartling('mktg_generics_sign_up_free')}
              </SignUpCta>
            </Box>
          </Box>
          {discoverySections.map(({ heading, html }, i) => (
            <Box key={heading}>
              {discoverySections.length > 2 && i === Math.floor(discoverySections.length / 2) && (
                <Box
                  css={theme => ({
                    borderTop: `2px solid ${theme.colors.coolGrey[60]}`,
                    borderBottom: `2px solid ${theme.colors.coolGrey[60]}`,
                    paddingTop: theme.space.s64,
                    paddingBottom: theme.space.s16,
                    marginTop: theme.space.s16,
                    marginBottom: theme.space.s48,
                    [theme.breakpoints.bp900.min]: {
                      marginBottom: theme.space.s120,
                    },
                  })}
                >
                  <Text
                    css={theme => ({
                      marginBottom: theme.space.s40,
                    })}
                    variant="paragraphM400"
                  >
                    {withSmartling('mktg_ct_disc_mid_page_cta_preblurb_scale')}
                  </Text>
                  <SignUpCta isDiscovery beaconElementDetail="register-middle" beaconElementLocation="body">
                    {dtTranslations?.want_text ?? withSmartling('mktg_generics_sign_up_free')}
                  </SignUpCta>
                </Box>
              )}
              <Box
                className={`${block}Section`}
                css={theme => ({
                  paddingTop: theme.space.s32,
                  margin: `${theme.space.s0} ${theme.space.s0} ${theme.space.s32}px`,
                  [theme.breakpoints.bp900.min]: {
                    paddingTop: theme.space.s80,
                    margin: `-${theme.space.s80}px ${theme.space.s0} ${theme.space.s64}px`,
                  },
                })}
                id={`section_${i}`}
              >
                <Heading as="h2" variant="headingXL">
                  {heading}
                </Heading>
                <Box css={userContentCss}>
                  <Box css={theme => theme.shared.htmlParserStyles}>{html && reactHTMLParser(html)}</Box>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
        <hr css={dividerCss} />
        {!!additionalResources.length && (
          <Box css={leftBlockCss}>
            <Heading
              as="h2"
              css={theme => ({
                margin: theme.space.s0,
                padding: theme.space.s0,
              })}
              variant="headingL"
            >
              {pre_footer_heading}
            </Heading>
            <Box
              css={{
                '&::before': {
                  top: 0,
                },
              }}
            >
              <ul
                css={theme => ({
                  listStyle: 'none',
                  marginBottom: theme.space.s48,
                })}
              >
                {additionalResources.map(({ default_link_text, final_url }) => (
                  <li
                    key={final_url}
                    css={theme => ({
                      margin: `${theme.space.s24}px ${theme.space.s0}`,
                      '&:last-child': {
                        marginBottom: theme.space.s0,
                      },
                    })}
                  >
                    <Link
                      beaconEvent={{
                        name: 'clickedMarketingPageElement',
                        elementLocation: 'body',
                        elementDetail: 'resources',
                        elementType: 'link',
                      }}
                      className={`${block}Jumplink`}
                      css={theme => ({
                        textAlign: 'left',
                        [theme.breakpoints.bp500.min]: {
                          textAlign: 'right',
                        },
                      })}
                      href={final_url}
                      iconAnimation="navigational"
                      variant="secondary"
                    >
                      {default_link_text}
                    </Link>
                  </li>
                ))}
              </ul>
            </Box>
          </Box>
        )}
        <Box
          css={theme => [
            additionalResources.length > 0
              ? rightBlockCss
              : { display: 'flex', flexDirection: 'column', [theme.breakpoints.bp900.min]: { marginLeft: '40%' } },
          ]}
        >
          <Box css={theme => theme.shared.htmlParserStyles}>
            {pre_footer_html && (
              <Text variant="paragraphM400">
                {reactHTMLParser(pre_footer_html, { replaceOptions: { replaceTag: 'span' } })}
              </Text>
            )}
          </Box>
          <Text variant="paragraphM400">
            {dtTranslations ? dtTranslations.want_text : withSmartling('mktg_ct_disc_bottom_cta_preblurb_default')}
          </Text>
          <Box css={theme => ({ marginTop: theme.space.s40 })}>
            <SignUpCta isDiscovery beaconElementDetail="register-bottom" beaconElementLocation="body">
              {dtTranslations?.make_text ?? withSmartling('mktg_generics_sign_up_free')}
            </SignUpCta>
          </Box>
        </Box>
      </Container>
    </Layout>
  );
};

export default Discovery;

export const Head: HeadFC<Lucid.Context.HeadData, Lucid.Context.HeadContext> = ({ ...props }) => (
  <LucidHead {...props} />
);

export const query = graphql`
  query DiscoveryQuery($nid: ID!, $shortCode: String!) {
    directus {
      node: discovery_by_id(id: $nid) {
        nodeTranslations: translations {
          path {
            url
          }
          languages_code {
            short_code
          }
          is_published
        }
        featured_image {
          ...mediaQuery
        }
        discovery_type {
          id
          image {
            ...mediaQuery
          }
          translations(filter: { languages_code: { short_code: { _eq: $shortCode } } }) {
            name
            make_text
            want_text
            discovery_links {
              links_id {
                beacon_id
                default_link_text
                final_url
              }
            }
            primary_link {
              beacon_id
              default_link_text
              final_url
            }
          }
        }
        translations(filter: { languages_code: { short_code: { _eq: $shortCode } } }) {
          heading
          intro
          html_sections {
            discovery_section_id {
              heading
              html
            }
          }
          pre_footer_heading
          pre_footer_html
        }
      }
    }
  }
`;
